import React, { useState } from "react";
import * as R from "ramda";
import { useTranslation } from "react-i18next";
import Dropdown from "react-bootstrap/Dropdown";
import moment from "moment";
import { Button } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import ConfirmDeleteSpecSheet from "../ui/ConfirmDeleteSpecSheet";
import CloneSpecsheetModal from "../specsheet/CloneSpecsheetModal";
import { validateValue } from "../../utils/utils";
import { scannedFrom } from "../../utils/extraValues";
import styled from "styled-components";
import add_spec from "../../images/Add_Spec_Sheet_Icon.png";
import clone_spec from "../../images/Clone_Spec_Sheet_Icon.png";
import edit from "../../images/edit_specsheet_logo.png";
import sleect from "../../images/select_spechseet_logo.png";
import {
  Printer,
  Pencil,
  ClockHistory,
  //FileEarmarkRichtext,
  Share,
  Download,
  InfoSquare,
  PlusCircle,
  Files,
  Camera,
} from "react-bootstrap-icons";
import PhotoSelect from "../ui/PhotoSelect";
import { getAppConfig } from "../api/apiConfig";
import { Spinner } from "react-bootstrap";
import { ModalSharing } from "../modal/ModalSharing";
import { ModalEmailConfirmation } from "../modal/ModalEmailConfirmation";
import { ModalPdfPreview } from "../modal/ModalPdfPreview";

const ContainerLogoStyle = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  flex: 0 0 auto;

  @media only screen and (max-width: 900px) and (orientation: portrait) {
    gap: 4px;
  }

  @media (max-width: 767px) {
    margin-left: 0;
    justify-content: center;
    flex: 1 1 100%;
  }
`;

const SpecsManageHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: transparent;
  width: 100%;
  height: auto;
  margin-bottom: 10px;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 767px) {
    justify-content: center;
    text-align: center;
  }
`;

const SpecsManageLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.8px;
  color: #6b7280;
  text-transform: uppercase;
  flex: 0 0 auto;
`;

const SpecsManageNameBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  flex: 1 1 auto;

  @media (max-width: 767px) {
    align-items: center;
    flex: 1 1 100%;
  }
`;

const ChipsRow = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
`;

const SpecsManageNameRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const SpecsManageNamePencil = styled.button`
  color: #00187e;
  background-color: transparent;
  border: none;
  padding: 2px 4px;
  line-height: 1;
  flex: 0 0 auto;
  cursor: pointer;
`;

const SpecsheetChip = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 2px 12px;
  border-radius: 999px;
  background: ${(props) => (props.$active ? "#e7eaf6" : "#f5f6fa")};
  /* $alert: left-handed, which the driller must see before touching the ball. */
  color: ${(props) => (props.$alert ? props.theme?.color?.error || "#D62828" : "#00187e")};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  border: 1px solid ${(props) => (props.$alert ? props.theme?.color?.error || "#D62828" : "#e5e7eb")};
  white-space: nowrap;
  cursor: ${(props) => (props.$readonly ? "default" : "pointer")};
  line-height: 1.6;

  &:hover {
    background: ${(props) => (props.$readonly ? props.$active ? "#e7eaf6" : "#f5f6fa" : "#d8defa")};
  }
`;

/**
 * Says this sheet was read off a photo rather than typed.
 *
 * Not a control -- there is nothing to change about where a record came
 * from -- so it is a span in chip's clothing, and quieter than the two
 * beside it, which are.
 */
const ScannedChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 12px;
  border-radius: 999px;
  background: #e8f5ee;
  color: #16803c;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  border: 1px solid #cfe8db;
  white-space: nowrap;
  line-height: 1.6;
`;

const ToolbarIconButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #00187e;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: rgba(0, 24, 126, 0.08);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  img {
    width: 20px;
    height: 20px;
    object-fit: contain;
  }

  svg {
    font-size: 18px;
    width: 18px;
    height: 18px;
  }
`;

// How many sheets are behind the clock. Without it the history icon looks the
// same whether the bowler has one sheet or six, so nobody opens it.
//
// Kept inside the 36px button rather than hanging off its corner: the toolbar
// sits at the top of a pane that clips its overflow on desktop landscape, and
// a badge that pokes out by even two pixels comes back sheared off. The white
// ring is what separates it from the icon underneath, in place of the gap.
const HistoryCountBadge = styled.span`
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 16px;
  height: 16px;
  padding: 0 3px;
  border-radius: 999px;
  background: #00187e;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  box-shadow: 0 0 0 2px #ffffff;
  pointer-events: none;
`;

const nameStyle = {
  verticalAlign: "middle",
  display: "flex",
  textAlign: "center",
  justifyContent: "center",
  fontSize: "14px",
  fontWeight: "900",
  color: "#000000",
  width: "100px",
};

const imgPrinterStyle = {
  width: "30px",
  height: "22px",
};

const labelPrinterStyle = {
  marginTop: "3px",
};

const logoStyle = {
  width: "20px",
  height: "20px",
  padding: "0",
  margin: "0 0 5px 0",
  cursosor: "pointer",
};
const specsManageContainerStyle = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  width: "100%",
};
const buttonLogoStyles = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  color: "#00197B",
  fontSize: "12px",
  fontWeight: "600",
  background: "transparent",
  border: "none",
};

const editNameStyle = {
  display: "flex",
  font: "normal normal bold 12px Poppins",
  letterSpacing: "0px",
  color: "#00147B",
};

const selectorContainerStyle = {
  width: "10px",
  border: "transparent",
  paddingLeft: "1.5rem",
  background: "none",
  backgroundPosition: "center",
  backgroundImage: `url(${sleect})`,
  backgroundSize: "auto",
  backgroundRepeat: "no-repeat",
  margin: "0",
  padding: "12px",
  marginTop: 7,
};

const muteTextStyle = {
  font: "normal normal normal 8px Poppins",
  letterSpacing: "0px",
  color: "#00000066",
};

export const SpecsheetManage = ({
  specsheets,
  specsheetAddNew,
  specsheetClone,
  customerPrint,
  specsheetSVGClick,
  isRightHander,
  specsheetSetSpecsheet,
  currentSpecsheet,
  balls,
  specSheetRemove,
  pluginSubscriptions,
  bowlerID,
  isReadOnly,
  userEmail,
  specSheetSendEmail,
  setShowSharingModal,
  specSheetGetAuditRecords,
  specsheetScanPhotoAI,
  savingPhotoAI,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [specSheetIdToDelete, setSpecSheetIdToDelete] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [showPhotoModal, setPhotoShowModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);

  const { t } = useTranslation();
  const idSelected = currentSpecsheet?.id || "";

  const handleSelect = (specsheet) => {
    specsheetSetSpecsheet({ specsheet });
    setShowDropdown(false);
  };

  const handleEditName = (e) => {
    if (isReadOnly) return;
    clickableRectOnClickHandler("name");
  };

  const clickableRectOnClickHandler = (field) => {
    if (isReadOnly) return;
    let openDropdown = field;
    if (!isRightHander) {
      if (field.indexOf("f1_") >= 0) {
        openDropdown = field.replace("f1_", "f2_");
      } else if (field.indexOf("f2_") >= 0) {
        openDropdown = field.replace("f2_", "f1_");
      }
    }
    specsheetSVGClick({ openDropdown });
  };

  const getDates = (specsheet) =>
    `CR: ${moment(specsheet.created_at).format("L")} UP: ${moment(
      specsheet.updated_at
    ).format("L")} @ ${moment(specsheet.updated_at).format("LT")}`;

  const renderSwitch = (id) => {
    const found = validateValue(specsheets?.find((spec) => spec.id === id));
    const spec = found !== "" ? found : specsheets[0];
    const displayName =
      validateValue(spec.name) === "" ? t("form.label.no_name") : spec.name;

    return (
      <>
        <SpecsManageNameRow>
          <span style={editNameStyle}>{displayName}</span>
          {!isReadOnly && (
            <SpecsManageNamePencil
              type="button"
              onClick={handleEditName}
              title={t("form.label.editName")}
              aria-label={t("form.label.editName")}
            >
              <Pencil />
            </SpecsManageNamePencil>
          )}
        </SpecsManageNameRow>
        <span style={muteTextStyle}>{getDates(spec)}</span>
      </>
    );
  };

  const SpecsheetDropdownItem = (props) => {
    const { specsheet } = props;

    return (
      <div
        class="dropdown-item"
        style={
          props.specsheet.id === idSelected
            ? { backgroundColor: "#00187E", cursor: "inherit", paddingRight: 0 }
            : {
                backgroundColor: "transparent",
                cursor: "inherit",
                paddingRight: 0,
              }
        }
        onClick={() => handleSelect(props.specsheet)}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <div style={{}}>
            <span
              style={
                props.specsheet.id === idSelected
                  ? { ...editNameStyle, color: "white" }
                  : editNameStyle
              }
            >
              {validateValue(specsheet.name) === ""
                ? t("form.label.no_name")
                : specsheet.name}
            </span>
            <span
              style={
                props.specsheet.id === idSelected
                  ? { ...muteTextStyle, color: "white" }
                  : muteTextStyle
              }
            >
              {getDates(specsheet)}
            </span>
          </div>
          {!isReadOnly && (
            <div style={{ width: 50, padding: 5, textAlign: "right" }}>
              <Button
                type="primary"
                danger
                shape="circle"
                size="small"
                onClick={() => {
                  setSpecSheetIdToDelete(props.specsheet.id);
                  setShowConfirmDelete(true);
                }}
              >
                <CloseOutlined style={{ fontSize: 16, verticalAlign: 0 }} />
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const specsheetCount = specsheets?.length ?? 0;

  const isLeftHanded = currentSpecsheet?.hand === "left_hander";
  const handLabel = currentSpecsheet?.hand
    ? t(currentSpecsheet.hand).toUpperCase()
    : t("right_hander").toUpperCase();
  const gripLabel = currentSpecsheet?.ball_grip
    ? t(currentSpecsheet.ball_grip).toUpperCase()
    : "";

  // Where this sheet came from, which nothing else records: created_by is the
  // operator either way, and the audit tables keep who and when but never
  // from what.
  const scan = scannedFrom(currentSpecsheet?.extra_values);
  const scannedAt = scan
    ? `${t("specsheetScan.scannedBadge")} · ${moment(scan.at).format("lll")}`
    : "";

  return (
    <div>
      <div className="row" style={specsManageContainerStyle}>
        <SpecsManageHeaderRow>
          <SpecsManageLabel>{t("Spec Sheet")}</SpecsManageLabel>

          <SpecsManageNameBlock>
            {renderSwitch(idSelected)}
          </SpecsManageNameBlock>

          <ChipsRow>
            {gripLabel && (
              <SpecsheetChip
                type="button"
                $readonly={isReadOnly}
                onClick={() => clickableRectOnClickHandler("ball_grip")}
                title={t("Grip Type")}
              >
                {gripLabel}
              </SpecsheetChip>
            )}
            <SpecsheetChip
              type="button"
              $readonly={isReadOnly}
              $alert={isLeftHanded}
              onClick={() => clickableRectOnClickHandler("hand")}
              title={t("Hand")}
            >
              {handLabel}
            </SpecsheetChip>
            {scannedAt && (
              <ScannedChip title={scannedAt}>
                {t("specsheetScan.scannedBadge")}
              </ScannedChip>
            )}
          </ChipsRow>

          <ContainerLogoStyle>
              <ToolbarIconButton
                onClick={() => setShowDropdown(true)}
                title={t("form.label.history")}
                aria-label={t("form.label.history")}
              >
                <Dropdown
                  show={showDropdown}
                  onToggle={(show) => setShowDropdown(show)}
                >
                  <Dropdown.Toggle
                    as={ClockHistory}
                    variant="secondary"
                    id="dropdown-basic"
                  />
                  <Dropdown.Menu>
                    <Dropdown.Header>
                      {t("form.label.select_spec")}
                    </Dropdown.Header>
                    {specsheets?.map((specsheet, index) => (
                      <Dropdown.Item
                        as={SpecsheetDropdownItem}
                        specsheet={specsheet}
                        key={specsheet.id || index}
                      />
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                {specsheetCount > 1 && (
                  <HistoryCountBadge>{specsheetCount}</HistoryCountBadge>
                )}
              </ToolbarIconButton>
              {!isReadOnly && (
                <>
                  <ToolbarIconButton
                    onClick={() => specsheetAddNew()}
                    title={t("form.label.add")}
                    aria-label={t("form.label.add")}
                  >
                    <PlusCircle />
                  </ToolbarIconButton>
                  <ToolbarIconButton
                    onClick={() => setShowCloneModal(true)}
                    disabled={!currentSpecsheet?.id}
                    title={t("form.label.clone")}
                    aria-label={t("form.label.clone")}
                  >
                    <Files />
                  </ToolbarIconButton>
                  {specsheetScanPhotoAI && currentSpecsheet?.id && (
                    <ToolbarIconButton
                      onClick={() => setPhotoShowModal(true)}
                      disabled={savingPhotoAI}
                      title={t("specsheetScan.scanFromPhoto")}
                      aria-label={t("specsheetScan.scanFromPhoto")}
                    >
                      {savingPhotoAI ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <Camera />
                      )}
                    </ToolbarIconButton>
                  )}
                </>
              )}
              <ToolbarIconButton
                onClick={() => {
                  if (currentSpecsheet?.id) setShowPdfPreview(true);
                }}
                disabled={!currentSpecsheet?.id}
                title={t("Print")}
                aria-label={t("Print")}
              >
                <Printer />
              </ToolbarIconButton>
              {pluginSubscriptions.indexOf("BOWLER_PLUS") >= 0 &&
                currentSpecsheet?.id && (
                  <ToolbarIconButton
                    onClick={() =>
                      setShowSharingModal({ showSharingModal: true })
                    }
                    title={t("form.label.share")}
                    aria-label={t("form.label.share")}
                  >
                    <Share />
                  </ToolbarIconButton>
                )}
              {currentSpecsheet?.id && specSheetGetAuditRecords && (
                <ToolbarIconButton
                  onClick={() =>
                    specSheetGetAuditRecords({ id: currentSpecsheet.id })
                  }
                  title={t("Info")}
                  aria-label={t("Info")}
                >
                  <InfoSquare />
                </ToolbarIconButton>
              )}
          </ContainerLogoStyle>
        </SpecsManageHeaderRow>
        <ConfirmDeleteSpecSheet
          show={showConfirmDelete}
          onHide={() => {
            setShowConfirmDelete(false);
          }}
          onConfirmClick={() => {
            specSheetRemove && specSheetRemove({ id: specSheetIdToDelete });
            setSpecSheetIdToDelete(null);
            setShowConfirmDelete(false);
          }}
        />
        <CloneSpecsheetModal
          show={showCloneModal}
          thumbCutCount={(currentSpecsheet?.thumb_cuts || []).length}
          onHide={() => setShowCloneModal(false)}
          onConfirm={(parts) => {
            setShowCloneModal(false);
            specsheetClone({
              specsheetToClone: {
                ...currentSpecsheet,
                customer_id: bowlerID,
              },
              thumbCuts: currentSpecsheet?.thumb_cuts,
              parts,
            });
          }}
        />
      </div>
      <PhotoSelect
        circle={false}
        show={showPhotoModal}
        showVideo={false}
        // Scanners hand people a PDF, not a photo. The reader takes
        // either; no video, since there is nothing to scan in one.
        accept=".jpg, .jpeg, .png, .pdf"
        onHide={() => {
          setPhotoShowModal(false);
        }}
        onFromCamera={(imageBase64, extension) => {
          specsheetScanPhotoAI({
            imageBase64,
            extension: extension || "png",
          });
          setPhotoShowModal(false);
        }}
        onFromGallery={(imageBase64, extension) => {
          specsheetScanPhotoAI({
            imageBase64,
            extension: extension || "png",
          });
          setPhotoShowModal(false);
        }}
      />
      <ModalSharing />
      <ModalEmailConfirmation />
      <ModalPdfPreview
        show={showPdfPreview}
        onHide={() => setShowPdfPreview(false)}
        pdfPath={
          currentSpecsheet?.id
            ? `render/specsheet/${bowlerID}/${currentSpecsheet.id}`
            : null
        }
        fileName={
          currentSpecsheet?.name
            ? `specsheet-${currentSpecsheet.name}`
            : "specsheet"
        }
      />
    </div>
  );
};
